// src/components/Extractors/HeaderExtract.js
import React, { useContext, useRef, useState } from "react";
import { ProjectContext } from "../../context/ProjectProvider";
import {
  createTemplateAPI,
  startDataExtractionAPI,
  getProjectTableAPI
} from "../../utils/api";
import "./HeaderExtract.css";
import * as XLSX from "xlsx";

const POLL_MS = 3000;            // ⏱ 3 s

const HeaderExtract = () => {
  const {
    /* panel y chat */
    isProjectListCollapsed,
    toggleProjectList,
    createNewChatSession,

    /* tablas */
    tables,
    setTables,
    activeTableId,
    currentProject,
    refreshTable,
    setPresetChatInput,
    //openErrorPopup,
  } = useContext(ProjectContext);

  /* ───── secundarios (chat / template) ───── */
  const [isCreatingChat, setIsCreatingChat]     = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  /* ───── pollers concurrentes por tabla ──── */
  const pollersRef = useRef({});      // { [tableId]: intervalId }

  /* helper: sólo cambia extractionStatus */
  const setStatus = (tableId, status) => {
    setTables(prev =>
      prev.map(t => (t.id === tableId ? { ...t, extractionStatus: status } : t))
    );
  };

  const stopPolling = (tableId) => {
    clearInterval(pollersRef.current[tableId]);
    delete pollersRef.current[tableId];
  };

  const startPolling = (tableId) => {
    if (pollersRef.current[tableId]) return;      // ya existe

    pollersRef.current[tableId] = setInterval(async () => {
      try {
        /* 1. GET /tables/{tableId} */
        const table  = await getProjectTableAPI(currentProject.id, tableId);
        const status = (table.extraction_status ?? "").trim().toLowerCase();

        /* 2. ¿terminó? → mar­camos y hacemos un pull final */
        if (status === "completed" || status === "failed") {
          setStatus(tableId, status);             // ← primero el estado
          await refreshTable(tableId);            // ← luego filas/summary
          stopPolling(tableId);
          return;
        }

        /* 3. Sigue en curso */
        setStatus(tableId, "in-progress");
        await refreshTable(tableId);              // refresco incremental
      } catch (err) {
        console.error(err);
        stopPolling(tableId);
        setStatus(tableId, "failed");
      }
    }, POLL_MS);
  };

  /* ───── RUN ───── */
  const handleRun = async () => {
    if (!activeTableId || !currentProject) return;

    const table = tables.find(t => t.id === activeTableId);
    if (!table || table.extractionStatus === "in-progress") return;

    setStatus(activeTableId, "in-progress");      // bloqueo inmediato
    try {
      await startDataExtractionAPI(currentProject.id, activeTableId);
      startPolling(activeTableId);
      } catch (err) {
          const code = err.response?.status || err.status;
    
          // 👉 429 -- sin tokens / cuota agotada
          if (code === 429) {
            setStatus(activeTableId, "failed");
            return;
          }
    
          // 409: ya había un proceso → engancha polling
          if (code === 409) startPolling(activeTableId);
          else {
            console.error(err);
            setStatus(activeTableId, "failed");
          }
    }
  };

  /* ───── Derivados UI ───── */
  const activeTable   = tables.find(t => t.id === activeTableId);
  const isRunningHere = activeTable?.extractionStatus === "in-progress";

  /* ───── Render ───── */
  return (
    <div className="group-header-center">
      {/* expand / collapse */}
      <button className="icon-button collapse-btn" onClick={toggleProjectList}>
        <i
          className={
            isProjectListCollapsed ? "bi bi-arrow-bar-right" : "bi bi-arrow-bar-left"
          }
        />
        <span className="button-tooltip">
          {isProjectListCollapsed ? "Expand" : "Collapse panel"}
        </span>
      </button>

      {/* botones derecha */}
      <div className="right-buttons">
      {/* botón ➜ Send to chat */}
        <button
          className="icon-button"
          onClick={() => {
            if (!activeTableId || !tables) return;

            const table = tables.find(t => t.id === activeTableId);
            if (!table || !table.rows) return;

            const rowsText = table.rows.map(row => {
              const group        = (row.groupName          ?? "").trim();
              const stringKW     = (row.stringKeywords     ?? "")
                                    .replace(/\n/g, ", ")
                                    .trim();
              const vectorKW     = (row.vectorKeywords     ?? "")
                                    .replace(/\n/g, ", ")
                                    .trim();
              const summaryInstr = (row.summaryInstructions ?? "")
                                    .replace(/\n/g, " ")
                                    .trim();
              const summary      = (row.extraction         ?? "").trim();

              return `Row: "${group}" | String Keywords: "${stringKW}" ` +
                    `| Vector Keywords: "${vectorKW}" ` +
                    `| Summary instructions: "${summaryInstr}" ` +
                    `| Extraction: "${summary}"`;
            });

            setPresetChatInput(rowsText.join("\n"));
          }}
        >
          <i className="bi bi-send-arrow-down" />
          <span className="button-tooltip">Send to chat</span>
        </button>


        {/* New chat */}
        <button
          className={`icon-button${isCreatingChat ? " disabled" : ""}`}
          onClick={async () => {
            if (isCreatingChat) return;
            setIsCreatingChat(true);
            try { await createNewChatSession(); }
            finally { setIsCreatingChat(false); }
          }}
          disabled={isCreatingChat}
        >
          {isCreatingChat ? (
            <div className="spinner-border spinner-border-sm" role="status" />
          ) : (
            <i className="bi bi-arrow-clockwise" />
          )}
          <span className="button-tooltip">New chat</span>
        </button>

        {/* Download (placeholder) */}
        <button
  className="icon-button"
  onClick={() => {
    const table = tables.find(t => t.id === activeTableId);
    if (!table || !table.rows) return;

    const data = table.rows.map(row => ({
      "Group Name"      : row.groupName || "",
      "String Keywords" : (row.stringKeywords || "").replace(/\n/g, ", "),
      "Vector Keywords" : (row.vectorKeywords || "").replace(/\n/g, ", "),
      "Summary instructions": row.summaryInstructions.replace(/\n/g," "),
      "Extraction"      : row.extraction
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Extracted Data");

    const filename = `${table.name || "extractors"}-results.xlsx`;
    XLSX.writeFile(workbook, filename);
  }}
>
  <i className="bi bi-download" />
  <span className="button-tooltip">Download results</span>
</button>

        {/* Save template */}
        <button
          className={`icon-button${isSavingTemplate ? " disabled" : ""}`}
          onClick={async () => {
            if (isSavingTemplate || !activeTable) return;
            setIsSavingTemplate(true);
            try {
              await createTemplateAPI({
                name: activeTable.name,
                clone_from_project_id: currentProject.id,
                clone_from_table_id: activeTable.id
              });
            } finally { setIsSavingTemplate(false); }
          }}
          disabled={isSavingTemplate}
        >
          {isSavingTemplate ? (
            <div className="spinner-border spinner-border-sm" role="status" />
          ) : (
            <i className="bi bi-cloud-arrow-up" />
          )}
          <span className="button-tooltip">Save template</span>
        </button>

        {/* RUN */}
        <button
          className={`run-btn ${!activeTableId || isRunningHere ? "disabled" : ""}`}
          onClick={handleRun}
          title="Run active table"
        >
          {isRunningHere ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
              </div>
            </div>
          ) : (
            <>
              <i className="bi bi-play-fill" />
              RUN
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default HeaderExtract;
